<template>
  <el-dialog
    :visible.sync="visible"
    :title="dialogTitle"
    width="400px"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    class="login-dialog"
    @closed="handleClosed"
  >
    <div class="login-body">
      <el-form :model="form" :rules="rules" ref="form" class="login-form">
        <!-- 账号密码登录 -->
        <template v-if="isLogin && loginType === 'password'">
          <el-form-item prop="username">
            <el-input
              v-model="form.username"
              placeholder="请输入账号/手机号"
              prefix-icon="el-icon-user"
              maxlength="20"
            ></el-input>
          </el-form-item>

          <el-form-item prop="password">
            <el-input
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="请输入密码"
              prefix-icon="el-icon-lock"
              @keyup.enter.native="handleSubmit"
            >
              <i slot="suffix"
                class="el-icon-view"
                @click="showPassword = !showPassword"
                style="cursor: pointer"
              ></i>
            </el-input>
          </el-form-item>
        </template>

        <!-- 短信验证码登录 -->
        <template v-else-if="isLogin && loginType === 'sms'">
          <el-form-item prop="mobile">
            <el-input
              v-model="form.mobile"
              placeholder="请输入手机号"
              prefix-icon="el-icon-mobile-phone"
              maxlength="11"
            ></el-input>
          </el-form-item>

          <el-form-item prop="code">
            <el-input
              v-model="form.code"
              placeholder="请输入验证码"
              prefix-icon="el-icon-key"
              @keyup.enter.native="handleSubmit"
            >
              <el-button
                slot="append"
                :disabled="codeDisabled"
                :loading="codeLoading"
                @click="getCode"
              >{{ codeDisabled ? count + 's' : '获取验证码' }}</el-button>
            </el-input>
          </el-form-item>
        </template>

        <!-- 注册 -->
        <template v-else>
          <el-form-item prop="mobile">
            <el-input
              v-model="form.mobile"
              placeholder="请输入手机号"
              prefix-icon="el-icon-mobile-phone"
              maxlength="11"
            ></el-input>
          </el-form-item>

          <el-form-item prop="password">
            <el-input
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="请输入密码（8-20位，含大小写字母和数字）"
              prefix-icon="el-icon-lock"
            >
              <i slot="suffix"
                class="el-icon-view"
                @click="showPassword = !showPassword"
                style="cursor: pointer"
              ></i>
            </el-input>
          </el-form-item>

          <el-form-item prop="code">
            <el-input
              v-model="form.code"
              placeholder="请输入短信验证码"
              prefix-icon="el-icon-key"
              @keyup.enter.native="handleSubmit"
            >
              <el-button
                slot="append"
                :disabled="codeDisabled"
                :loading="codeLoading"
                @click="getCode"
              >{{ codeDisabled ? count + 's' : '获取验证码' }}</el-button>
            </el-input>
          </el-form-item>
        </template>

        <el-form-item>
          <el-button
            type="primary"
            class="login-btn"
            :loading="loading"
            @click="handleSubmit"
          >
            {{ submitText }}
          </el-button>
        </el-form-item>
      </el-form>

      <div class="login-switch" v-if="isLogin">
        <a href="javascript:void(0)" @click="switchLoginType">
          {{ loginType === 'password' ? '手机验证码登录' : '账号密码登录' }}
        </a>
      </div>

      <div class="login-footer">
        <span v-if="isLogin">
          还没有账号？
          <a href="javascript:void(0)" @click="switchMode(false)">立即注册</a>
        </span>
        <span v-else>
          已有账号？
          <a href="javascript:void(0)" @click="switchMode(true)">立即登录</a>
        </span>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { setToken, setUserInfo } from '@/utils/auth'

const validateMobile = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入手机号'))
  } else if (!/^1[3-9]\d{9}$/.test(value)) {
    callback(new Error('请输入正确的11位手机号'))
  } else {
    callback()
  }
}

const validatePassword = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入密码'))
  } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[^]{8,20}$/.test(value)) {
    callback(new Error('密码须为8-20位，且包含大小写字母和数字'))
  } else {
    callback()
  }
}

export default {
  name: 'LoginDialog',
  data () {
    return {
      visible: false,
      isLogin: true,
      loginType: 'password',
      showPassword: false,
      loading: false,
      codeLoading: false,
      codeDisabled: false,
      count: 60,
      timer: null,
      form: {
        username: '',
        password: '',
        mobile: '',
        code: ''
      }
    }
  },
  computed: {
    dialogTitle () {
      return this.isLogin ? '登录' : '注册'
    },
    submitText () {
      return this.isLogin ? '登 录' : '注 册'
    },
    rules () {
      const passwordRules = [
        { required: true, message: '请输入密码', trigger: 'blur' }
      ]
      if (!this.isLogin) {
        passwordRules.push({ validator: validatePassword, trigger: 'blur' })
      }
      return {
        username: [
          { required: true, message: '请输入账号/手机号', trigger: 'blur' }
        ],
        password: passwordRules,
        mobile: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { validator: validateMobile, trigger: 'blur' }
        ],
        code: [
          { required: true, message: '请输入短信验证码', trigger: 'blur' }
        ]
      }
    }
  },
  created () {
    // 全局打开登录弹窗：this.$bus.$emit('open-login', 'login' | 'register')
    this.$bus.$on('open-login', this.open)
  },
  methods: {
    open (mode) {
      this.switchMode(mode !== 'register')
      this.visible = true
    },
    handleClosed () {
      this.resetForm()
    },
    resetForm () {
      this.showPassword = false
      this.codeDisabled = false
      this.codeLoading = false
      this.loading = false
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      this.count = 60
      this.form = {
        username: '',
        password: '',
        mobile: '',
        code: ''
      }
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    switchMode (isLogin) {
      this.isLogin = isLogin
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    switchLoginType () {
      this.loginType = this.loginType === 'password' ? 'sms' : 'password'
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    // 获取短信验证码：短信登录用 sendMobileCode，注册用 sendRegMobileCode
    getCode () {
      if (!/^1[3-9]\d{9}$/.test(this.form.mobile)) {
        this.$message.error('请输入正确的手机号')
        return
      }
      const api = this.isLogin
        ? '/crmapi/p/message/mobile/sendMobileCode'
        : '/crmapi/p/message/mobile/sendRegMobileCode'
      this.codeLoading = true
      this.$http({
        url: this.$http.adornUrl(api + '?mobile=' + this.form.mobile),
        method: 'get'
      })
        .then(({ data }) => {
          if (data && data.code === 'C0000') {
            this.$message.success('验证码已发送')
            this.codeDisabled = true
            this.count = 60
            this.timer = setInterval(() => {
              if (this.count > 0) {
                this.count--
              } else {
                this.codeDisabled = false
                clearInterval(this.timer)
              }
            }, 1000)
          } else {
            this.$message.error((data && data.message) || '发送失败')
          }
        })
        .finally(() => {
          this.codeLoading = false
        })
    },
    handleSubmit () {
      this.$refs.form.validate(valid => {
        if (!valid) return

        this.loading = true
        let req

        if (this.isLogin) {
          if (this.loginType === 'password') {
            req = {
              url: this.$http.adornUrl('/crmapi/gateway/auth'),
              method: 'post',
              data: {
                username: this.form.username,
                password: this.form.password,
                loginType: 'pc'
              }
            }
          } else {
            req = {
              url: this.$http.adornUrl('/crmapi/p/user/phoneLogin'),
              method: 'post',
              data: {
                mobile: this.form.mobile,
                captcha: this.form.code
              }
            }
          }
        } else {
          req = {
            url: this.$http.adornUrl('/crmapi/p/user/reg'),
            method: 'post',
            data: {
              tenantId: 3133981754195968,
              source: 'web',
              loginName: this.form.mobile,
              mobile: this.form.mobile,
              realName: this.form.mobile, // 姓名默认为手机号
              password: this.form.password,
              captcha: this.form.code
            }
          }
        }

        this.$http(req)
          .then(({ data }) => {
            if (data && data.code === 'C0000') {
              if (this.isLogin) {
                // 登录：auth 成功后写入租户，写入成功才算登录成功
                this.checkTenant(data.result)
              } else {
                this.$message.success(data.message || '注册成功，请登录')
                this.switchMode(true)
                this.loading = false
              }
            } else {
              this.$message.error((data && data.message) || '操作失败')
              this.loading = false
            }
          })
          .catch(() => {
            this.loading = false
          })
      })
    },
    checkTenant (result) {
      result = result || {}
      if (!result.token) {
        this.$message.error('登录失败，未获取到用户信息')
        this.loading = false
        return
      }
      setToken(result.token)
      this.$http({
        url: this.$http.adornUrl('/crmapi/p/tenant/checkTenant'),
        method: 'post',
        data: { id: 3133981754195968 }
      }).then(({ data }) => {
        if (data && data.code === 'C0000') {
          this.loginSuccess(result)
        } else {
          this.$message.error((data && data.message) || '租户写入失败，请重试')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    loginSuccess (result) {
      result = result || {}
      if (!result.token) {
        this.$message.error('登录失败，未获取到用户信息')
        return
      }
      const auth = result.loginAuthDto || {}
      setToken(result.token)
      setUserInfo({
        id: auth.accountId || auth.id || '',
        accountId: auth.accountId || '',
        realName: auth.realName || auth.mobile || '',
        mobile: auth.mobile || '',
        avatarUrl: auth.avatarUrl || ''
      })
      this.$message.success('登录成功')
      this.visible = false
      // 通知各页面登录状态已变化
      this.$bus.$emit('login-success')
    }
  },
  beforeDestroy () {
    this.$bus.$off('open-login', this.open)
    if (this.timer) clearInterval(this.timer)
  }
}
</script>

<style lang="scss">
.login-dialog {
  .el-dialog__header {
    border-bottom: 1px solid #f0f0f0;
  }
}

.login-body {
  padding: 8px 4px 0;
}

.login-form {
  .el-form-item {
    margin-bottom: 18px;
  }

  .el-input {
    .el-input__inner {
      height: 44px;
      line-height: 44px;
    }

    .el-input__prefix {
      left: 12px;
    }

    .el-input-group__append {
      background: #fff;
      padding: 0 12px;

      .el-button {
        border: none;
        color: #667eea;
      }
    }
  }
}

.login-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
}

.login-switch {
  text-align: center;
  margin-top: -6px;
  margin-bottom: 4px;

  a {
    color: #667eea;
    text-decoration: none;
    font-size: 13px;

    &:hover {
      text-decoration: underline;
    }
  }
}

.login-footer {
  text-align: center;
  margin-top: 12px;
  font-size: 14px;
  color: #666;

  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/LoginDialog.vue